import { createSlice } from '@reduxjs/toolkit';

import { authApi } from '@/features/auth/api/authApi';
import { workspacesApi } from '@/features/workspaces/api/workspacesApi';

import type { Workspace } from '@web/types';

type WorkspacesState = {
  lastCreatedName: string | null;
  invitesSent: number;
  currentWorkspace: Workspace | null;
};

type WorkspaceWithLogoFields = Workspace & {
  logo_asset_key?: string | null;
  logo_url?: string | null;
  avatar_url?: string | null;
  avatar?: string | null;
};

const initialState: WorkspacesState = {
  lastCreatedName: null,
  invitesSent: 0,
  currentWorkspace: null,
};

function mergeCurrentWorkspace(
  currentWorkspace: Workspace | null,
  patch: Workspace | null | undefined,
): Workspace | null {
  if (patch == null) {
    return null;
  }

  return {
    ...(currentWorkspace ?? {}),
    ...patch,
  } as Workspace;
}

export const workspacesSlice = createSlice({
  name: 'workspaces',
  initialState,
  reducers: {
    setLastCreatedName: (state, action: { payload: string | null }) => {
      state.lastCreatedName = action.payload;
    },
    incrementInvites: (state) => {
      state.invitesSent += 1;
    },
    setCurrentWorkspace: (state, action: { payload: Workspace | null }) => {
      state.currentWorkspace = action.payload;
    },
    resetInvites: (state) => {
      state.invitesSent = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // * Sync currentWorkspace from auth flows.
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
        const workspace = (action.payload as any)?.data?.workspace;
        state.currentWorkspace = workspace ?? null;
      })
      .addMatcher(authApi.endpoints.verify.matchFulfilled, (state, action) => {
        const workspace = (action.payload as any)?.data?.workspace;
        state.currentWorkspace = workspace ?? null;
      })
      .addMatcher(
        authApi.endpoints.register.matchFulfilled,
        (state, action) => {
          const workspace = (action.payload as any)?.data?.workspace;
          state.currentWorkspace = workspace ?? null;
        },
      )
      .addMatcher(
        authApi.endpoints.switchWorkspace.matchFulfilled,
        (state, action) => {
          const workspace = (action.payload as any)?.data?.workspace;
          state.currentWorkspace = workspace ?? null;
        },
      )
      .addMatcher(
        workspacesApi.endpoints.acceptInvite.matchFulfilled,
        (state, action) => {
          const workspace = (action.payload as any)?.data?.workspace;
          state.currentWorkspace = workspace ?? null;
        },
      )
      // * Keep in sync when workspace is updated during onboarding
      .addMatcher(
        workspacesApi.endpoints.updateCurrentWorkspace.matchFulfilled,
        (
          state: WorkspacesState,
          action: { payload?: { data?: Workspace | null } },
        ) => {
          state.currentWorkspace = mergeCurrentWorkspace(
            state.currentWorkspace,
            action.payload?.data,
          );
        },
      )
      .addMatcher(
        workspacesApi.endpoints.patchCurrentWorkspace.matchFulfilled,
        (
          state: WorkspacesState,
          action: {
            payload?: { data?: Workspace | null };
            meta?: {
              arg?: {
                originalArgs?: {
                  logo_asset_key?: string | null;
                };
              };
            };
          },
        ) => {
          const nextWorkspace = mergeCurrentWorkspace(
            state.currentWorkspace,
            action.payload?.data,
          );

          if (!nextWorkspace) {
            state.currentWorkspace = null;
            return;
          }

          const requestedLogoRemoval =
            action.meta?.arg?.originalArgs?.logo_asset_key === null;
          if (requestedLogoRemoval) {
            const mutableWorkspace = nextWorkspace as WorkspaceWithLogoFields;
            mutableWorkspace.logo_asset_key = '';
            mutableWorkspace.logo_url = '';
            mutableWorkspace.avatar_url = '';
            mutableWorkspace.avatar = '';
          }

          state.currentWorkspace = nextWorkspace;
        },
      )
      .addMatcher(
        workspacesApi.endpoints.getCurrentWorkspace.matchFulfilled,
        (
          state: WorkspacesState,
          action: { payload?: { data?: Workspace | null } },
        ) => {
          state.currentWorkspace = mergeCurrentWorkspace(
            state.currentWorkspace,
            action.payload?.data,
          );
        },
      );
  },
});

export const {
  setLastCreatedName,
  incrementInvites,
  resetInvites,
  setCurrentWorkspace,
} = workspacesSlice.actions;
export default workspacesSlice.reducer;
